import { useState, FormEvent, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'

// Sentinel for optional Select fields. Radix Select reserves '' as the no-value state,
// so we round-trip null↔NONE through this placeholder.
const NONE = '__none__'

type Account = {
  id: number
  code: string
  name: string
  account_type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'REVENUE' | 'COST_OF_SALES' | 'EXPENSE'
  parent: number | null
  parent_code: string | null
  description: string
  is_active: boolean
  is_system: boolean
  normal_balance: 'DEBIT' | 'CREDIT'
  has_children: boolean
}

type AccountInput = {
  code: string
  name: string
  account_type: Account['account_type']
  parent: number | null
  description: string
  is_active: boolean
}

const emptyAccount: AccountInput = {
  code: '',
  name: '',
  account_type: 'ASSET',
  parent: null,
  description: '',
  is_active: true,
}

const TYPE_COLORS: Record<Account['account_type'], string> = {
  ASSET: 'bg-blue-100 text-blue-800',
  LIABILITY: 'bg-orange-100 text-orange-800',
  EQUITY: 'bg-purple-100 text-purple-800',
  REVENUE: 'bg-green-100 text-green-800',
  COST_OF_SALES: 'bg-amber-100 text-amber-800',
  EXPENSE: 'bg-red-100 text-red-800',
}

const TYPE_LABEL: Record<Account['account_type'], string> = {
  ASSET: 'Asset',
  LIABILITY: 'Liability',
  EQUITY: 'Equity',
  REVENUE: 'Revenue',
  COST_OF_SALES: 'Cost of Sales',
  EXPENSE: 'Expense',
}

export default function FinanceAccounts() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: async () => (await api.get<Account[]>('/finance/accounts/')).data,
  })

  const [editing, setEditing] = useState<Account | null>(null)
  const [creating, setCreating] = useState(false)
  const [filterType, setFilterType] = useState<string>('ALL')

  const createMut = useMutation({
    mutationFn: async (payload: AccountInput) => (await api.post('/finance/accounts/', payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'accounts'] })
      setCreating(false)
    },
  })
  const updateMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: AccountInput }) =>
      (await api.patch(`/finance/accounts/${id}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'accounts'] })
      setEditing(null)
    },
  })
  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/finance/accounts/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['finance', 'accounts'] }),
  })

  const accounts = data || []
  const filtered = useMemo(() => {
    if (filterType === 'ALL') return accounts
    return accounts.filter((a) => a.account_type === filterType)
  }, [accounts, filterType])

  // Indent rows based on parent depth (computed)
  const depthOf = useMemo(() => {
    const byId: Record<number, Account> = {}
    accounts.forEach((a) => (byId[a.id] = a))
    const compute = (a: Account): number => {
      let depth = 0
      let cur = a
      while (cur.parent && byId[cur.parent]) {
        depth++
        cur = byId[cur.parent]
        if (depth > 10) break // safety
      }
      return depth
    }
    const out: Record<number, number> = {}
    accounts.forEach((a) => (out[a.id] = compute(a)))
    return out
  }, [accounts])

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Chart of Accounts</h2>
          <p className="text-sm text-gray-500">
            {accounts.length} accounts ({accounts.filter((a) => a.is_active).length} active)
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>+ Add account</Button>
      </div>

      <div className="mb-4 flex items-center space-x-2 text-sm">
        <span className="text-gray-500">Filter:</span>
        {(['ALL', 'ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'COST_OF_SALES', 'EXPENSE'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setFilterType(t)}
            className={`px-3 py-1 rounded ${
              filterType === t ? 'bg-gray-800 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            {t === 'ALL' ? 'All' : TYPE_LABEL[t]}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Type</Th>
                <Th>Balance</Th>
                <Th>Active</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <Td className="font-mono">{a.code}</Td>
                  <Td>
                    <span style={{ paddingLeft: `${(depthOf[a.id] || 0) * 16}px` }}>{a.name}</span>
                    {a.is_system && (
                      <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">sys</span>
                    )}
                  </Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded ${TYPE_COLORS[a.account_type]}`}>
                      {TYPE_LABEL[a.account_type]}
                    </span>
                  </Td>
                  <Td className="text-xs text-gray-500">{a.normal_balance}</Td>
                  <Td>
                    <span className={`inline-block w-2 h-2 rounded-full ${a.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                  </Td>
                  <Td>
                    <button onClick={() => setEditing(a)} className="text-blue-600 hover:underline text-sm mr-3">
                      Edit
                    </button>
                    {!a.is_system && !a.has_children && (
                      <button
                        onClick={() => {
                          if (confirm(`Delete ${a.code} ${a.name}?`)) deleteMut.mutate(a.id)
                        }}
                        className="text-red-600 hover:underline text-sm"
                      >
                        Delete
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <AccountDialog
          initial={
            editing
              ? {
                  code: editing.code,
                  name: editing.name,
                  account_type: editing.account_type,
                  parent: editing.parent,
                  description: editing.description,
                  is_active: editing.is_active,
                }
              : emptyAccount
          }
          isEdit={!!editing}
          allAccounts={accounts.filter((a) => !editing || a.id !== editing.id)}
          submitting={createMut.isPending || updateMut.isPending}
          error={(createMut.error as any) || (updateMut.error as any)}
          onCancel={() => { setCreating(false); setEditing(null) }}
          onSubmit={(payload) => {
            if (editing) updateMut.mutate({ id: editing.id, payload })
            else createMut.mutate(payload)
          }}
        />
      )}
    </AdminLayout>
  )
}

function AccountDialog({
  initial, isEdit, allAccounts, onSubmit, onCancel, submitting, error,
}: {
  initial: AccountInput
  isEdit: boolean
  allAccounts: Account[]
  onSubmit: (p: AccountInput) => void
  onCancel: () => void
  submitting: boolean
  error: any
}) {
  const [form, setForm] = useState<AccountInput>(initial)
  const set = <K extends keyof AccountInput>(k: K, v: AccountInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  // parent options should match the chosen type
  const parentOptions = allAccounts.filter((a) => a.account_type === form.account_type)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    onSubmit(form)
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{isEdit ? 'Edit account' : 'Add account'}</h3>
        <form onSubmit={submit}>
          <FieldSet>
            <FieldLegend>Account details</FieldLegend>
            <FieldDescription>
              Define how this account fits in the chart of accounts.
            </FieldDescription>
            <FieldGroup>
              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <FieldLabel htmlFor="account-code">Code</FieldLabel>
                  <Input
                    id="account-code"
                    type="text"
                    required
                    value={form.code}
                    onChange={(e) => set('code', e.target.value)}
                    placeholder="6100"
                    className="font-mono"
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="account-type">Type</FieldLabel>
                  <Select
                    value={form.account_type}
                    onValueChange={(v) => set('account_type', v as AccountInput['account_type'])}
                  >
                    <SelectTrigger id="account-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ASSET">Asset</SelectItem>
                      <SelectItem value="LIABILITY">Liability</SelectItem>
                      <SelectItem value="EQUITY">Equity</SelectItem>
                      <SelectItem value="REVENUE">Revenue</SelectItem>
                      <SelectItem value="COST_OF_SALES">Cost of Sales</SelectItem>
                      <SelectItem value="EXPENSE">Expense</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field>
                <FieldLabel htmlFor="account-name">Name</FieldLabel>
                <Input
                  id="account-name"
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="account-parent">Parent account</FieldLabel>
                <SearchableSelect
                  id="account-parent"
                  value={form.parent ? String(form.parent) : NONE}
                  onChange={(v) => set('parent', v === NONE ? null : Number(v))}
                  options={[
                    { value: NONE, label: '— None (top level) —' },
                    ...parentOptions.map((p) => ({
                      value: String(p.id), label: p.name, hint: p.code,
                    })),
                  ]}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="account-description">Description</FieldLabel>
                <Textarea
                  id="account-description"
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  rows={2}
                />
              </Field>
              <label className="flex items-center space-x-2 pt-1">
                <input
                  type="checkbox" checked={form.is_active}
                  onChange={(e) => set('is_active', e.target.checked)}
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>

              {error && (
                <div className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded">
                  {String(error?.response?.data ? JSON.stringify(error.response.data) : error.message || error)}
                </div>
              )}
            </FieldGroup>
          </FieldSet>

          <div className="flex justify-end gap-2 pt-3">
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{children}</th>
}
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-sm ${className || ''}`}>{children}</td>
}
